import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  Alert, InputAdornment, IconButton, CircularProgress,
  Divider, Stepper, Step, StepLabel,
} from '@mui/material';
import {
  Person, Email, Lock, Visibility, VisibilityOff,
  AutoAwesome, Key, ArrowForward, ArrowBack,
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STEPS = ['Your Details', 'Verify Email'];

export default function RegisterPage() {
  const navigate    = useNavigate();
  const { setUser } = useAuth();

  const [step, setStep]         = useState(0);
  const [form, setForm]         = useState({ name: '', email: '', password: '', confirm: '' });
  const [otp, setOtp]           = useState('');
  const [showPw, setShowPw]     = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');
  const [resendTimer, setTimer] = useState(0);

  const handle = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const startResendTimer = () => {
    setTimer(60);
    const interval = setInterval(() => {
      setTimer(t => {
        if (t <= 1) { clearInterval(interval); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      const res = await authAPI.sendRegisterOtp({
        name: form.name, email: form.email, password: form.password,
      });
      setSuccess(`OTP sent to ${res.data.data?.email || form.email}`);
      setStep(1);
      startResendTimer();
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError('Please enter the 6-digit OTP'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authAPI.verifyRegisterOtp({
        name: form.name, email: form.email,
        password: form.password, otp,
      });
      const { accessToken, user: userData } = res.data.data;
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setError(''); setSuccess('');
    try {
      await authAPI.sendRegisterOtp({
        name: form.name, email: form.email, password: form.password,
      });
      setSuccess('New OTP sent successfully!');
      startResendTimer();
    } catch {
      setError('Failed to resend OTP. Please try again.');
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse at 80% 50%, rgba(2,136,209,0.08) 0%, transparent 60%), radial-gradient(ellipse at 20% 20%, rgba(103,58,183,0.06) 0%, transparent 60%), #000000',
    }}>
      {/* Stars */}
      <Box sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none',
        backgroundImage: 'radial-gradient(1px 1px at 15% 25%, rgba(255,255,255,0.4) 0%, transparent 100%), radial-gradient(1px 1px at 60% 75%, rgba(255,255,255,0.3) 0%, transparent 100%), radial-gradient(1.5px 1.5px at 75% 10%, rgba(255,255,255,0.5) 0%, transparent 100%)',
      }} />

      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 480, px: 2 }}>

        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%', mb: 2,
            bgcolor: 'rgba(2,136,209,0.15)', border: '1px solid rgba(255,255,255,0.3)' }}>
            <AutoAwesome sx={{ fontSize: 36, color: 'primary.main' }} />
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.light', mb: 0.5 }}>
            Join SpacePulse
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Create your space explorer account
          </Typography>
        </Box>

        <Card sx={{ background: 'rgba(10,22,40,0.85)', backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255,255,255,0.15)' }}>
          <CardContent sx={{ p: 4 }}>

            {/* Stepper */}
            <Stepper activeStep={step} sx={{ mb: 3 }}>
              {STEPS.map(label => (
                <Step key={label}>
                  <StepLabel sx={{
                    '& .MuiStepLabel-label':            { color: 'text.secondary', fontSize: 12 },
                    '& .MuiStepLabel-label.Mui-active': { color: 'primary.light' },
                    '& .MuiStepIcon-root.Mui-active':   { color: 'primary.main' },
                    '& .MuiStepIcon-root.Mui-completed':{ color: '#66bb6a' },
                  }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

            {/* ── Step 1: Fill Details ── */}
            {step === 0 && (
              <form onSubmit={handleSendOtp}>
                <TextField
                  fullWidth name="name" label="Full Name"
                  value={form.name} onChange={handle}
                  required autoFocus sx={{ mb: 2 }}
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start">
                      <Person sx={{ color: 'primary.main', fontSize: 20 }} />
                    </InputAdornment>
                  )}}
                />
                <TextField
                  fullWidth name="email" label="Email Address" type="email"
                  value={form.email} onChange={handle}
                  required sx={{ mb: 2 }}
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start">
                      <Email sx={{ color: 'primary.main', fontSize: 20 }} />
                    </InputAdornment>
                  )}}
                />
                <TextField
                  fullWidth name="password" label="Password"
                  type={showPw ? 'text' : 'password'}
                  value={form.password} onChange={handle}
                  required sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock sx={{ color: 'primary.main', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPw(v => !v)} size="small">
                          {showPw ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth name="confirm" label="Confirm Password"
                  type={showPw ? 'text' : 'password'}
                  value={form.confirm} onChange={handle}
                  required sx={{ mb: 3 }}
                  error={form.confirm !== '' && form.password !== form.confirm}
                  helperText={form.confirm !== '' && form.password !== form.confirm ? 'Passwords do not match' : ''}
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: 'primary.main', fontSize: 20 }} />
                    </InputAdornment>
                  )}}
                />
                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  disabled={loading} endIcon={<ArrowForward />}
                  sx={{ py: 1.5, fontSize: 15, mb: 2 }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Send Verification OTP'}
                </Button>
              </form>
            )}

            {/* ── Step 2: Enter OTP ── */}
            {step === 1 && (
              <form onSubmit={handleVerifyOtp}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography sx={{ fontSize: 48, mb: 1 }}>📧</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Verification code sent to
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'primary.light', fontWeight: 700 }}>
                    {form.email}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Check your inbox and spam folder
                  </Typography>
                </Box>

                <TextField
                  fullWidth label="Enter 6-Digit OTP"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required autoFocus sx={{ mb: 2 }}
                  inputProps={{
                    maxLength: 6,
                    style: { textAlign: 'center', fontSize: 32, letterSpacing: 14, fontWeight: 800 },
                  }}
                  InputProps={{ startAdornment: (
                    <InputAdornment position="start">
                      <Key sx={{ color: 'primary.main' }} />
                    </InputAdornment>
                  )}}
                />

                {/* OTP progress dots */}
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mb: 2 }}>
                  {Array(6).fill(0).map((_, i) => (
                    <Box key={i} sx={{
                      width: 10, height: 10, borderRadius: '50%',
                      bgcolor: i < otp.length ? 'primary.main' : 'rgba(255,255,255,0.2)',
                      transition: 'all 0.2s',
                    }} />
                  ))}
                </Box>

                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  disabled={loading || otp.length !== 6}
                  sx={{ py: 1.5, fontSize: 15, mb: 2 }}
                >
                  {loading ? <CircularProgress size={22} color="inherit" /> : 'Verify & Create Account'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    startIcon={<ArrowBack />}
                    onClick={() => { setStep(0); setOtp(''); setError(''); setSuccess(''); }}
                    sx={{ color: 'text.secondary', textTransform: 'none' }}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleResend}
                    disabled={resendTimer > 0}
                    sx={{
                      textTransform: 'none',
                      color: resendTimer > 0 ? 'text.secondary' : 'primary.light',
                    }}
                  >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                  </Button>
                </Box>
              </form>
            )}

            <Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.1)' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', px: 1 }}>OR</Typography>
            </Divider>

            <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>
              Already have an account?{' '}
              <span
                onClick={() => navigate('/login')}
                style={{ color: '#ffffff', fontWeight: 600, cursor: 'pointer' }}
              >
                Sign in
              </span>
            </Typography>

          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
